import { CommerceProperty } from '~/interfaces/simple-product';
import {
  PaymentError,
  RetrievePaymentResultSettings,
  TenderLine,
} from '~/interfaces/commerce-types';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { CartTenderLine } from '~/interfaces/checkout';
import { Maybe, UsAePayErrorMessage } from '~/gql/__generated__/types';

export enum PaymentConnectorPostMessageType {
  CardPrefix = 'msax-cc-cardprefix',
  Error = 'msax-cc-error',
  Height = 'msax-cc-height',
  Result = 'msax-cc-result',
  Submit = 'msax-cc-submit',
  ExtraContext = 'msax-cc-extracontext',
  Redirect = 'msax-cc-redirect',
  Showoverlay = 'msax-cc-showoverlay',
  Hideoverlay = 'msax-cc-hideoverlay',
  ShippingAddress = 'msax-shipping-address',
  SynchronyClose = 'synchrony-modal-closed',
  IS_CREDIT_CARD_VALID = 'usaEpayValid',
  ADYEN_VALIDATION = 'msax-cc-validation',
}

/**
 * @Response_Type IGetCardPaymentAcceptPointResponse API
 * @type IGetCardPaymentAcceptPointResponse
 * @URL /Commerce/Carts('195ebc39-ace3-444a-bbf6-d0de59a177ec')/GetCardPaymentAcceptPoint?api-version=7.3
 */
export interface IGetCardPaymentAcceptPointResponse {
  AcceptPageUrl: string;
  AcceptPageSubmitUrl?: string;
  MessageOrigin?: string;
  AcceptPageContent: string;
  NotReloadAcceptPageContentWhenAmountChanged?: boolean;
  PaymentConnectorId?: string;
}

export interface IPaymentConnector {
  connectorHtml: IGetCardPaymentAcceptPointResponse;
  tenderType: string;
  config: {
    heading: string;
    subHeading: string;
    synchronySwitch: boolean;
    synchronyButtonText: string;
    applyNowLink: string;
  };
}

/**
 * CardPaymentAcceptSettings entity interface.
 */
export interface CardPaymentAcceptSettings {
  HostPageOrigin?: string;
  AdaptorPath?: string;
  CardTokenizationEnabled?: boolean;
  CardPaymentEnabled?: boolean;
  PaymentAmount?: number;
  HideBillingAddress?: boolean;
  TenderTypeId?: string;
  Locale?: string;
  RequireShippingAddress?: boolean;
}

/**
 * @Payload_Type GetCardPaymentAcceptPoint API
 * @URL /Commerce/Carts('195ebc39-ace3-444a-bbf6-d0de59a177ec')/GetCardPaymentAcceptPoint?api-version=7.3
 */
export interface IGetCardPaymentAcceptPointPayload {
  cardPaymentAcceptSettings: CardPaymentAcceptSettings | null;
  extensionProperties?: CommerceProperty[];
}

/**
 * @Payload_Type RetrieveCardPaymentAcceptResult API
 * @URL /Commerce/Carts/RetrieveCardPaymentAcceptResult?api-version=7.3
 */
export interface IRetrieveCardPaymentAcceptResultPayload {
  resultAccessCode: string | null;
  extensionProperties?: CommerceProperty[];
  cartId: string;
  settings?: RetrievePaymentResultSettings;
}

export interface IPaymentFrameElementProps {
  iframeAriaLabel: string;
  displayContent: string;
  sourceUrl: string;
  requestUrlOrigin?: string;
  messageOrigin?: string;
  paymentType: string;
  isForterValidationEnabled: boolean;
  cartId: string;
  orderDetails: ISalesOrderResponse;
  usaEPayErrorMessages?: Maybe<Maybe<UsAePayErrorMessage>[]> | undefined;
  isForterValidationEnabledForAdyen?: boolean;
}

export interface IPaymentConnectorPostMessage {
  message: string;
  targetOrigin: string;
}

/**
 * @Payload_Type RetrieveCardPaymentAcceptResult API
 * @URL /Commerce/Carts/RetrieveCardPaymentAcceptResult?api-version=7.3
 */
export interface IRetrieveCardPaymentAcceptResultPayload {
  resultAccessCode: string | null;
  extensionProperties?: CommerceProperty[];
  cartId: string;
  settings?: RetrievePaymentResultSettings;
}

/**
 * CardPaymentAcceptResult entity interface.
 */
export interface CardPaymentAcceptResult {
  TenderLine?: TenderLine;
  TokenizedPaymentCard?: TokenizedPaymentCard;
  PaymentSdkErrors?: PaymentError[];
  AdditionalContext?: string;
  AccessCode?: string;
  Settings?: RetrievePaymentResultSettings;
  Email?: string;
  errCode?: string;
  Code?: string;
}

export interface IEventResult {
  type: string;
  value: string;
  id: string;
}

/**
 * CardTokenInfo entity interface.
 */
interface CardTokenInfo {
  CardToken?: string;
  UniqueCardId?: string;
  ServiceAccountId?: string;
  MaskedCardNumber?: string;
}

/**
 * TokenizedPaymentCard entity interface.
 */
export interface TokenizedPaymentCard {
  IsSwipe?: boolean;
  TenderType?: string;
  CardTokenInfo?: CardTokenInfo;
  Phone?: string;
  Country?: string;
  House?: string;
  Address1?: string;
  Address2?: string;
  City?: string;
  State?: string;
  Zip?: string;
  NameOnCard?: string;
  CardTypeId?: string;
  ExpirationMonth?: number;
  ExpirationYear?: number;
  ExtensionProperties?: CommerceProperty[];
}

/**
 * PaymentCard entity interface.
 */
export interface PaymentCard {
  UseShippingAddress?: boolean;
  CardNumber?: string;
  CCID?: string;
  Track1?: string;
  Track2?: string;
  Track3?: string;
  VoiceAuthorizationCode?: string;
  EncryptedPin?: string;
  AdditionalSecurityData?: string;
  IsSwipe?: boolean;
  Phone?: string;
  Country?: string;
  House?: string;
  Address1?: string;
  Address2?: string;
  City?: string;
  State?: string;
  Zip?: string;
  NameOnCard?: string;
  CardTypeId?: string;
  ExpirationMonth?: number;
  ExpirationYear?: number;
  ExtensionProperties?: CommerceProperty[];
}

export interface ISubmitPaymentPayload {
  cartId: string | undefined;
  receiptEmail: string | undefined;
  cartTenderLines: CartTenderLine[] | undefined;
  cartVersion: number | undefined;
  orderBalanceDetails: string;
}

export interface ISyfDetails {
  planOfferKey: string;
  offerName: string;
}

export interface IPaymentState {
  isBelowMinimumAmount: boolean;
  isOtherAmountSelected: boolean;
  isBalanceSelected: boolean;
  hasPartialPayment: boolean;
  isStoreBlockingSynchrony: boolean;
  isStoreBlockingProgressive: boolean;
  isStoreExcludedFromFinancing: boolean;
  isStoreBlockingAffirm?: boolean;
}
